import { Badge, Box, Container, Grid, Group, Stack, Text, Title } from '@mantine/core';
import {
  IconAlertTriangleFilled,
  IconBrain,
  IconCircleCheckFilled,
  IconShieldCheckFilled,
} from '@tabler/icons-react';

import { Reveal } from '@/components/motion/Reveal';
import classes from './Safety.module.css';

interface Tier {
  n: 1 | 2 | 3;
  title: string;
  body: string;
  accent: 'safety' | 'warn' | 'ai';
}

const TIERS: Tier[] = [
  {
    n: 1,
    title: 'Deterministic rules',
    body:
      'Hard limits and combination checks encoded as Rust types. Unit-testable, auditable, never hallucinated. Safe / Caution / Warning / Danger.',
    accent: 'safety',
  },
  {
    n: 2,
    title: 'Statistical guardrails',
    body:
      'Variance and delta checks against the base map and population norms for your vehicle profile. Flags outliers before they reach the ECU.',
    accent: 'warn',
  },
  {
    n: 3,
    title: 'AI advisor',
    body:
      'Claude explains and suggests — gated behind Tier 1 + 2. It can illuminate, not override. The deterministic verdict always has the last word.',
    accent: 'ai',
  },
];

const ACCENT: Record<Tier['accent'], { color: string; ring: string; bg: string; Icon: typeof IconShieldCheckFilled }> = {
  safety: {
    color: 'var(--tf-safety)',
    ring: 'rgba(0, 230, 118, 0.35)',
    bg: 'rgba(0, 230, 118, 0.08)',
    Icon: IconShieldCheckFilled,
  },
  warn: {
    color: 'var(--tf-warn)',
    ring: 'rgba(255, 179, 0, 0.35)',
    bg: 'rgba(255, 179, 0, 0.08)',
    Icon: IconAlertTriangleFilled,
  },
  ai: {
    color: 'var(--tf-ai)',
    ring: 'rgba(187, 134, 252, 0.35)',
    bg: 'rgba(187, 134, 252, 0.08)',
    Icon: IconBrain,
  },
};

export function Safety() {
  return (
    <Box component="section" id="safety" py={{ base: 64, md: 96 }} className={classes.wrap} aria-labelledby="safety-title">
      <Container size="xl">
        <Grid gap={{ base: 'xl', md: 64 }} align="center">
          <Grid.Col span={{ base: 12, md: 5 }}>
            <Stack gap="md">
              <Reveal>
                <Badge
                  variant="outline"
                  color="safety"
                  size="lg"
                  radius="sm"
                  leftSection={<IconShieldCheckFilled size={14} />}
                  styles={{
                    root: {
                      borderColor: 'rgba(0, 230, 118, 0.4)',
                      color: 'var(--tf-safety)',
                      background: 'rgba(0, 230, 118, 0.06)',
                    },
                  }}
                >
                  Three-tier safety model
                </Badge>
              </Reveal>
              <Reveal delay={0.05}>
                <Title id="safety-title" order={2} className={classes.heading}>
                  Safety isn&rsquo;t a feature. It&rsquo;s the architecture.
                </Title>
              </Reveal>
              <Reveal delay={0.1}>
                <Text c="var(--tf-text-secondary)" lh={1.6}>
                  Every change you make is graded by a deterministic Rust engine before it ever
                  reaches the LLM. Only <strong>Danger</strong> blocks. <strong>Warning</strong>{' '}
                  requires a logged acknowledgement. <strong>Caution</strong> and <strong>Safe</strong>{' '}
                  flow through. The AI can explain what the engine decided — it cannot reverse it.
                </Text>
              </Reveal>
              <Reveal delay={0.15}>
                <Group gap="xs" mt="md">
                  <IconCircleCheckFilled size={20} color="var(--tf-safety)" />
                  <Text size="sm" c="var(--tf-text-primary)" fw={600}>
                    Auditable. Reproducible. Tested.
                  </Text>
                </Group>
              </Reveal>
            </Stack>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 7 }}>
            <Stack gap="md">
              {TIERS.map((tier, idx) => {
                const a = ACCENT[tier.accent];
                return (
                  <Reveal key={tier.n} delay={0.05 + idx * 0.08}>
                    <Box
                      className={classes.tier}
                      style={{
                        borderColor: a.ring,
                        background: `linear-gradient(180deg, ${a.bg} 0%, rgba(20, 20, 31, 0.7) 100%)`,
                      }}
                    >
                      <Group gap="md" align="flex-start" wrap="nowrap">
                        <Box
                          className={classes.tierIcon}
                          style={{ background: a.bg, borderColor: a.ring, color: a.color }}
                          aria-hidden="true"
                        >
                          <a.Icon size={22} stroke={1.8} />
                        </Box>
                        <Stack gap={4} style={{ flex: 1 }}>
                          <Group gap="xs">
                            <Text
                              size="xs"
                              tt="uppercase"
                              lts="0.12em"
                              fw={700}
                              style={{ color: a.color }}
                            >
                              Tier {tier.n}
                            </Text>
                          </Group>
                          <Title order={3} fz="h5" c="var(--tf-text-primary)">
                            {tier.title}
                          </Title>
                          <Text size="sm" c="var(--tf-text-secondary)" lh={1.55}>
                            {tier.body}
                          </Text>
                        </Stack>
                      </Group>
                    </Box>
                  </Reveal>
                );
              })}
            </Stack>
          </Grid.Col>
        </Grid>
      </Container>
    </Box>
  );
}
